/**
 * Extracted content, rendered as stored.
 *
 * Every string that came out of a filing goes through here. The component
 * does three things and refuses to do a fourth:
 *
 *  - sets `lang` from the *content's* language, not the UI's, so the Arabic
 *    font stack applies to an Arabic table inside English chrome;
 *  - sets `dir` from the same, or `dir="auto"` when the language is unknown,
 *    letting the browser do the bidi work per string rather than the app
 *    guessing (§11.1);
 *  - marks the text as raw, so it is never reflowed or renumbered.
 *
 * What it will not do is reformat. `١٬٤٢٣٬٢١٥٬٢٥٧` is stored as one string
 * with Arabic-indic digits and Arabic thousands separators, and it is
 * rendered as that string. Passing it through Intl is exactly the operation
 * that would hide a transliteration error.
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '~/lib/cn.ts'

export type ContentLang = string | null | undefined

function attrs(lang: ContentLang) {
  const l = lang?.trim().toLowerCase()
  if (l === 'ar') return { lang: 'ar', dir: 'rtl' as const }
  if (l === 'en') return { lang: 'en', dir: 'ltr' as const }
  // Unknown: let the browser decide per string.
  return { dir: 'auto' as const }
}

export interface RawProps extends Omit<HTMLAttributes<HTMLElement>, 'dir' | 'lang'> {
  lang?: ContentLang
  as?: 'span' | 'div' | 'p' | 'td' | 'th' | 'h3' | 'h4'
  children: ReactNode
}

export const Raw = forwardRef<HTMLElement, RawProps>(function Raw(
  { lang, as: As = 'span', className, children, ...rest },
  ref,
) {
  return (
    <As ref={ref as never} className={cn('content-raw', className)} {...attrs(lang)} {...rest}>
      {children}
    </As>
  )
})

/**
 * A printed figure. Same rule as `Raw`, plus: numbers inside an RTL cell are
 * isolated so a Latin-digit figure next to Arabic text does not reorder.
 */
export function RawNumber({
  value, lang, className, title,
}: { value: string | null | undefined; lang?: ContentLang; className?: string; title?: string }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-[var(--text-faint)]">—</span>
  }
  return (
    <bdi
      title={title}
      className={cn('tnum whitespace-nowrap', className)}
      {...attrs(lang)}
    >
      {value}
    </bdi>
  )
}

/**
 * A value the app could not translate -- an unknown page type, a check name
 * the pipeline added last week. Shown verbatim in a muted monospace, because
 * showing it is right and showing nothing is not (§11.2).
 */
export function Untranslated({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <code
      dir="ltr"
      className={cn(
        'rounded-[2px] bg-[var(--bg-sunken)] px-1 py-px',
        'font-[family-name:var(--font-mono)] text-[length:var(--text-tiny)] text-[var(--text-muted)]',
        className,
      )}
    >
      {children}
    </code>
  )
}
